import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { PatchAnnouncementPayload, PatchStatusAcceptPayload } from '@edtech-ui/core/models/material-upload/announcement-patch-api';
import { GetAllMaterialData, GetAllMaterialResponse } from '@edtech-ui/core/models/material-upload/get-all-materials';
import { AnnouncementService } from '@edtech-ui/core/services/announcement-management/announcement.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, Subscription, tap } from 'rxjs';
import { DeleteDialogBoxComponent } from '../delete-dialog-box/delete-dialog-box.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
	selector: 'app-classroom-show-all-announcement',
	standalone: true,
	imports: [ReactiveFormsModule, MatProgressSpinnerModule, MatMenuModule, CommonModule, MatIconModule],
	templateUrl: './classroom-show-all-announcement.component.html',
	styleUrl: './classroom-show-all-announcement.component.scss'
})
export class ClassroomShowAllAnnouncementComponent implements OnInit, OnDestroy {

	_studyMaterialData: GetAllMaterialData[] = [];
	subscription = new Subscription();
	currentPopup: number | null = null;
	editAnnouncementForm: FormGroup;
	classroomId: number = 0;
	isSentAnnouncement: boolean = false;
	isReject: boolean = false;
	isAccept: boolean = false;
	currentIndex!: number;

	constructor(
		private _announcementService: AnnouncementService,
		private _dialog: MatDialog,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
	) {
		this.editAnnouncementForm = new FormGroup({
			content: new FormControl('', Validators.required),
		});

		this.subscription.add(this._announcementService.reload$.pipe(tap(() => this.getAllMaterials())).subscribe());

	}

	ngOnInit(): void {
		const id = this.route.parent?.snapshot.paramMap.get('id');
		if (id) {
			this.classroomId = Number(id);
		}
		this.getAllMaterials();

	}

	getAllMaterials() {
		if (!this.classroomId) {
			this._toastr.error('classroomId is missing');
			return;
		}
		this.isSentAnnouncement = true;

		this.subscription.add(
			this._announcementService.getAllMaterialByClassId(this.classroomId, true).subscribe({
				next: (response: GetAllMaterialResponse) => {
					if (response.Data && response.Success) {
						this._studyMaterialData = response.Data;
					} else {
						this._studyMaterialData = [];
					}
					this.isSentAnnouncement = false;
				},
				error: () => {
					this.isSentAnnouncement = false;
					this._studyMaterialData = [];
					this._toastr.error('Failed to fetch sent announcements. Please try again.');
				}
			})
		);
	}

	openEditPopup(announcement: any) {
		this.currentPopup = announcement.id;
		this.editAnnouncementForm.patchValue({
			content: announcement.content
		});
	}

	closePopup(event?: Event) {
		if (event) {
			event.stopPropagation();
		}
		this.currentPopup = null;
		this.editAnnouncementForm.reset();
	}

	updateAnnouncement() {
		if (!this.currentPopup) return;

		const payload: PatchAnnouncementPayload = {
			announcement_content: this.editAnnouncementForm.value.content,
			announcement_id: this.currentPopup,
		}
		this.subscription.add(
			this._announcementService.updateAnnouncement(payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success(response.Message);
						this.getAllMaterials(); 
					}
				})).subscribe(() => {
					this.closePopup();
				})
		);
	}

	openDeleteDialogBox(announcementId: number) {
		this._dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { announcementId }
		}).afterClosed().subscribe(result => {
			if (result == true) {
				this.deleteMaterialById(announcementId);
			}
		});
	}

	deleteMaterialById(announcementId: number) {
		this.subscription.add(
			this._announcementService.deleteMaterialById(announcementId).subscribe({
				next: () => {
					this._toastr.success('Announcement deleted successfully');
				},
				error:()=>{
					  this._toastr.error('Failed to delete announcement. Please try again.');
				}
			})
		);
	}

	updateAnnouncementStatus(announcementId: number, isAccepted: boolean | null, currentIndex: number) {
		const payload: PatchStatusAcceptPayload = {
			announcement_id: announcementId,
			class_id: Number(this.classroomId),
			is_accepted: isAccepted,
		}

		this.currentIndex = currentIndex;

		if (isAccepted) {
			this.isAccept = true;
		} else {
			this.isReject = true;
		}

		this.subscription.add(
			this._announcementService.updateAcceptAnnouncementStatus(payload).pipe(
				tap(response => {
					if (response.Success) {
						if (isAccepted) {
							this._toastr.success('Announcement accepted successfully');
						} else {
							this._toastr.error('Announcement not accepted');
						}
					} else {
						this._toastr.error(response.Message);
					}
					this.isAccept = false;
					this.isReject = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong.Please try again');
					this.isAccept = false;
					this.isReject = false;
					return of(null);
				})
			).subscribe());
	}

	convertToIST(utcDateString: string): string {
		if (!utcDateString) return '';
		const date = new Date(utcDateString + 'Z');
		return new Intl.DateTimeFormat('en-IN', {
			timeZone: 'Asia/Kolkata',
			dateStyle: 'medium',
			timeStyle: 'short'
		}).format(date);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}